/** Modale Fenster der App.
 *
 *  Es gibt zwei Ebenen. Ebene 1 ist das Arbeitsfenster (Formular, Reiter, Tabelle),
 *  Ebene 2 liegt darueber und ist fuer Rueckfragen und kurze Hinweise gedacht, die
 *  aus einem offenen Fenster heraus kommen. Ohne die zweite Ebene muesste ein
 *  Bestaetigungsdialog das Arbeitsfenster verdraengen, weil beide denselben Platz
 *  im DOM haetten.
 *
 *  Schliessen kennt zwei Wege: close() geschieht auf Wunsch des Codes und ist
 *  endgueltig (Speichern, Abbrechen), requestClose() geschieht auf Wunsch des
 *  Benutzers (X, Escape, Klick auf den Hintergrund) und darf von einem
 *  Schliess-Schutz abgelehnt werden, etwa bei ungespeicherten Aenderungen.
 */

interface LayerIds { backdrop: string; modal: string; title: string; body: string; close: string; }

export interface ModalOptions {
  wide?: boolean;
  /** Etwas breiter als wide (56rem), fuer Rueckfragen mit mehreren ausfuehrlich beschrifteten Knoepfen */
  wider?: boolean;
  fixedSize?: boolean;
  xl?: boolean;
  /**
   * Haelt die letzte Aktionsleiste am unteren Rand fest, waehrend der Inhalt
   * darunter durchlaeuft. Gegenstueck zum ohnehin fixierten Kopf, gedacht fuer
   * lange Formulare ohne Reiter. Opt-in, weil ein Dialog mehrere Aktionsleisten
   * haben kann und nur die aeusserste gemeint ist. Fuer Dialoge MIT Reitern gibt
   * es fixedSize.
   */
  stickyActions?: boolean;
}

/** Rueckgabe false haelt das Fenster offen. Darf nachfragen und deshalb asynchron sein. */
export type CloseGuard = () => boolean | Promise<boolean>;

const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 18 18 6M6 6l12 12"/></svg>`;

function createLayer(ids: LayerIds) {
  let ensured = false;

  /**
   * Ruestet die Ebene nach, falls sie im ausgelieferten HTML fehlt. initModal()
   * ist die erste Anweisung im Start der App: laufen index.html und Bundle einmal
   * auseinander, wuerde eine fehlende Ebene sonst nicht nur den Dialog kosten,
   * sondern den gesamten Start.
   */
  const ensure = () => {
    if (ensured) return;
    ensured = true;
    if (document.getElementById(ids.backdrop)) return;
    const el = document.createElement("div");
    el.id = ids.backdrop;
    el.className = "modal-backdrop hidden";
    el.innerHTML = `<div id="${ids.modal}" class="modal">
      <div class="modal-header">
        <h2 id="${ids.title}"></h2>
        <button id="${ids.close}" class="btn-icon">${CLOSE_ICON}</button>
      </div>
      <div class="modal-body" id="${ids.body}"></div>
    </div>`;
    document.body.appendChild(el);
  };

  const byId = (id: string) => { ensure(); return document.getElementById(id)!; };
  const backdrop = () => byId(ids.backdrop);
  const modal = () => byId(ids.modal);
  const titleEl = () => byId(ids.title);
  const bodyEl = () => byId(ids.body);
  const closeBtn = () => byId(ids.close);

  let onCloseCallback: (() => void) | null = null;
  let closeGuard: CloseGuard | null = null;
  let guardRunning = false;

  const open = (titleText: string, html: string, opts?: ModalOptions) => {
    // Ein neuer Dialog erbt nichts vom vorherigen. Sonst ueberlebt ein Callback
    // oder ein Schliess-Schutz den Wechsel und greift im falschen Fenster.
    onCloseCallback = null;
    closeGuard = null;
    guardRunning = false;

    titleEl().textContent = titleText;
    bodyEl().innerHTML = html;
    modal().classList.toggle("narrow", !opts?.wide && !opts?.wider && !opts?.xl);
    modal().classList.toggle("modal-wider", !!opts?.wider);
    modal().classList.toggle("modal-xl", !!opts?.xl);
    modal().classList.toggle("modal-fixed", !!opts?.fixedSize);
    modal().classList.toggle("modal-sticky-actions", !!opts?.stickyActions);
    backdrop().classList.remove("hidden");
  };

  const close = () => {
    backdrop().classList.add("hidden");
    closeGuard = null;
    guardRunning = false;
    // Erst abraeumen, dann melden: der Callback darf selbst wieder ein Fenster
    // oeffnen, ohne dass ihm dessen frisch gesetzter Callback geloescht wird.
    const cb = onCloseCallback;
    onCloseCallback = null;
    cb?.();
  };

  const requestClose = async () => {
    // Waehrend die Rueckfrage laeuft, prallt ein zweites Escape ab. Sonst
    // stapeln sich mehrere Rueckfragen fuer denselben Schliessversuch.
    if (guardRunning) return;
    if (closeGuard) {
      guardRunning = true;
      let mayClose: boolean;
      try {
        mayClose = await closeGuard();
      } finally {
        guardRunning = false;
      }
      if (!mayClose) return;
    }
    close();
  };

  return {
    open, close, requestClose,
    body: () => bodyEl(),
    setTitle: (titleText: string) => { titleEl().textContent = titleText; },
    onClose: (fn: () => void) => { onCloseCallback = fn; },
    setGuard: (fn: CloseGuard | null) => { closeGuard = fn; },
    isOpen: () => !backdrop().classList.contains("hidden"),
    init: () => {
      closeBtn().addEventListener("click", () => { void requestClose(); });
      backdrop().addEventListener("click", (e) => { if (e.target === backdrop()) void requestClose(); });
    },
  };
}

const layer1 = createLayer({ backdrop: "modal-backdrop", modal: "modal", title: "modal-title", body: "modal-body", close: "modal-close" });
const layer2 = createLayer({ backdrop: "modal2-backdrop", modal: "modal2", title: "modal2-title", body: "modal2-body", close: "modal2-close" });

// ── Ebene 1: das Arbeitsfenster ──

export function openModal(titleText: string, html: string, opts?: ModalOptions) { layer1.open(titleText, html, opts); }
export function closeModal() { layer1.close(); }
export function modalBody(): HTMLElement { return layer1.body(); }
export function onModalClose(fn: () => void) { layer1.onClose(fn); }
/** Titel eines offenen Modals nachtraeglich aendern, ohne den Inhalt neu zu schreiben. */
export function setModalTitle(titleText: string) { layer1.setTitle(titleText); }
export function isModalOpen(): boolean { return layer1.isOpen(); }

/**
 * Schliess-Schutz fuer das offene Fenster. Wird nur bei X, Escape und Klick auf
 * den Hintergrund befragt, nicht bei closeModal(): Speichern und Abbrechen sind
 * bewusste Entscheidungen und brauchen keine Rueckfrage. Gilt bis zum Schliessen
 * oder bis zum naechsten openModal().
 */
export function setCloseGuard(fn: CloseGuard | null) { layer1.setGuard(fn); }

// ── Ebene 2: Rueckfragen ueber einem offenen Fenster ──

export function openModal2(titleText: string, html: string, opts?: ModalOptions) { layer2.open(titleText, html, opts); }
export function closeModal2() { layer2.close(); }
export function modalBody2(): HTMLElement { return layer2.body(); }
export function onModal2Close(fn: () => void) { layer2.onClose(fn); }
export function isModal2Open(): boolean { return layer2.isOpen(); }

// Init Listeners (einmal aufrufen)
export function initModal() {
  layer1.init();
  layer2.init();
  // Escape trifft immer die oberste offene Ebene.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (layer2.isOpen()) { void layer2.requestClose(); return; }
    if (layer1.isOpen()) void layer1.requestClose();
  });
}
